/* What chat's live connection does next - the same rules as the mobile app's
   (care_diary_app_rn/lib/chatConnectionPolicy.ts; change one, change both), kept apart from
   the socket so they can be tested on their own. The server stays the one that decides who
   may read what: these only decide when to try again, and when trying again can't help. */

// Where reconnecting stopped for good, until a new login: null, "denied" or "session-ended"

/* ----- The server closed the connection. It says why first (chat:closing) ----- */
export const afterServerClose = (reason, { accessChanged, loginRunningOut }) => {
  // A role or permission edit: the new handshake re-checks what I may read
  if (accessChanged) return "reconnect-now";
  switch (reason) {
    // Renewed already (an API call did it meanwhile): straight back with the new one
    case "login-expired":
      return loginRunningOut ? "renew" : "reconnect-now";
    // Logged out or switched company in this browser: the new login (if any) reconnects
    case "session-ended":
      return "stop-session";
    // Archived, or no chat.read any more
    case "staff-inactive":
    case "no-chat-read":
      return "stop-denied";
    // A failed setup, or a close with no reason given: may pass. A login that has run out
    // meanwhile is renewed rather than waited out
    default:
      return loginRunningOut ? "renew" : "retry-later";
  }
};

/* ----- A connection attempt was refused ----- */
export const afterConnectError = (message, { loginRunningOut, renewedAlready }) => {
  if (message === "Busy") return "retry-later";
  if (message !== "Unauthorized") return "none"; // a transport error: socket.io retries itself
  // Run out: renewed once per login. Refused again with a fresh one - it isn't the login
  if (loginRunningOut && !renewedAlready) return "renew";
  if (loginRunningOut) return "stop-session";
  // A current login refused: archived, removed, or no chat.read
  return "stop-denied";
};

/* ----- Renewing the login failed ----- */
export const afterRenewFailure = (error) => {
  const status = error?.response?.status;
  // The server refused the refresh: the session is over (the next API call signs out)
  if (status === 401 || status === 403) return "stop-session";
  // No network, a timeout, a server error: may pass
  return "retry-later";
};

/* ----- Access refused can be given back. Asked again - a whole new connection, checked by
   the server from scratch - when the tab comes back into view or the network returns, at
   most once a minute, and on "Try again"; never on a timer. An ended session needs a new
   login ----- */
export const DENIED_RECHECK_MS = 60 * 1000;

/* ----- Back in view / network back: keep a connection that's up, connect one that isn't ----- */
export const onForeground = ({ connected, hasToken, problem, sinceRefusedMs = 0 }) => {
  if (connected) return "reuse";
  if (!hasToken || problem === "session-ended") return "none";
  if (problem === "denied" && sinceRefusedMs < DENIED_RECHECK_MS) return "none";
  return "connect";
};

/* ----- Spaced-out tries for things that may pass: 5 s, 10 s, 20 s... up to a minute, each
   with up to half again at random so everyone doesn't come back at once ----- */
export const RETRY_BASE_MS = 5000;
export const RETRY_MAX_MS = 60000;
export const retryDelay = (attempt, random) => {
  const doubled = 2 ** Math.max(0, attempt);
  const base = Math.min(RETRY_BASE_MS * doubled, RETRY_MAX_MS);
  const jitter = base * 0.5 * random;
  return Math.round(base + jitter);
};

/* ----- The notice over the chat: "none", "connecting", "reconnecting", "offline", "denied"
   or "session-ended" ----- */

// A drop shorter than this is never mentioned - a reconnect usually takes well under it
export const NOTICE_GRACE_MS = 4000;
// The first connection for a login gets longer before it counts as a problem - and is
// "Connecting...", never "Reconnecting..."
export const FIRST_CONNECT_GRACE_MS = 8000;

export const connectionNotice = ({ connected, everConnected, online, problem, downForMs }) => {
  if (connected) return "none";
  // Refused access and an ended session are said straight away - never held back
  if (problem) return problem;
  const grace = everConnected ? NOTICE_GRACE_MS : FIRST_CONNECT_GRACE_MS;
  if (downForMs < grace) return "none";
  if (online === false) return "offline";
  return everConnected ? "reconnecting" : "connecting";
};

// When the notice could next change by itself (the grace running out), from the same state
export const noticeRecheckInMs = ({ connected, everConnected, problem, downForMs }) => {
  if (connected || problem) return null;
  const grace = everConnected ? NOTICE_GRACE_MS : FIRST_CONNECT_GRACE_MS;
  return downForMs < grace ? grace - downForMs : null;
};
